/**
 * PocketBase API client for Stack Forward website
 */

import type { PocketBasePayload, ApiError, UTMParams } from '../types';

/**
 * Configuration for PocketBase API
 */
const API_CONFIG = {
  timeout: 10000, // 10 seconds
  headers: {
    'Content-Type': 'application/json',
  },
} as const;

/**
 * Extract UTM parameters from current URL
 */
export function extractUTMParams(): UTMParams {
  const urlParams = new URLSearchParams(window.location.search);

  return {
    source: urlParams.get('utm_source') || undefined,
    medium: urlParams.get('utm_medium') || undefined,
    campaign: urlParams.get('utm_campaign') || undefined,
    term: urlParams.get('utm_term') || undefined,
    content: urlParams.get('utm_content') || undefined,
  };
}

/**
 * Submit contact form data to PocketBase
 * @param data - Form data to submit
 * @returns Promise that resolves to success or rejects with error
 */
export async function submitContact(data: PocketBasePayload): Promise<void> {
  const pbUrl = import.meta.env.VITE_PB_URL;

  if (!pbUrl) {
    throw new Error('PocketBase URL not configured');
  }

  // Validate honeypot field
  if (data.__hp && data.__hp.trim() !== '') {
    // Silently abort if honeypot is filled (anti-spam)
    console.warn('Honeypot field filled - submission blocked');
    return;
  }

  // Remove honeypot from payload before sending
  const { __hp, ...payload } = data;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const response = await fetch(`${pbUrl}/api/collections/contacts/records`, {
      method: 'POST',
      headers: API_CONFIG.headers,
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData: ApiError = await response.json().catch(() => ({
        code: response.status,
        message: `HTTP ${response.status}: ${response.statusText}`,
      }));

      throw new Error(errorData.message || 'Failed to submit contact form');
    }

    // Success - no need to return data for contact form
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }
      throw error;
    }

    throw new Error('An unexpected error occurred');
  }
}

/**
 * Check PocketBase connection health
 * @returns Promise that resolves to true if healthy, false otherwise
 */
export async function checkHealth(): Promise<boolean> {
  const pbUrl = import.meta.env.VITE_PB_URL;

  if (!pbUrl) {
    return false;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    const response = await fetch(`${pbUrl}/api/health`, {
      method: 'GET',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Interest option structure
 */
export interface InterestOption {
  id: string;
  value: string;
  label: string;
  description?: string;
  link?: string;
  order: number;
  category?: string;
}

/**
 * Fetch interest options from PocketBase master table
 * @returns Promise that resolves to array of interest options
 */
export async function fetchInterestOptions(): Promise<InterestOption[]> {
  const pbUrl = import.meta.env.VITE_PB_URL;

  if (!pbUrl) {
    // Fallback to hardcoded options if PB URL not configured
    return getFallbackOptions();
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    // Fetch all records (perPage=100 to get all 41 records)
    const response = await fetch(
      `${pbUrl}/api/collections/interest_options/records?sort=order&perPage=100`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error('Failed to fetch interest options');
    }

    const data = await response.json();
    return data.items || [];
  } catch (error) {
    console.warn(
      'Failed to fetch interest options from PocketBase, using fallback:',
      error
    );
    // Return fallback options
    return getFallbackOptions();
  }
}

/**
 * Get fallback interest options
 * These serve as a backup when PocketBase is not configured or unavailable
 */
function getFallbackOptions(): InterestOption[] {
  return [
    {
      id: '1',
      value: 'apache-superset',
      label: 'Apache Superset',
      description:
        'Modern, open-source data visualization and exploration platform for building interactive dashboards on large datasets.',
      link: 'https://superset.apache.org',
      order: 1,
    },
    {
      id: '2',
      value: 'bookstack',
      label: 'BookStack',
      description:
        'An open-source documentation and wiki platform designed for easy organization, editing, and sharing of internal documentation.',
      link: 'https://www.bookstackapp.com',
      order: 2,
    },
    {
      id: '3',
      value: 'clickhouse',
      label: 'ClickHouse',
      description:
        'High-performance columnar database optimized for online analytical processing (OLAP) and big data workloads.',
      link: 'https://clickhouse.com',
      order: 3,
    },
    {
      id: '4',
      value: 'erpnext',
      label: 'ERPNext',
      description:
        'ERPNext is a full-featured open-source ERP system by Frappe Technologies for managing accounting, inventory, HR, CRM, manufacturing, and more.',
      link: 'https://erpnext.com',
      order: 4,
    },
    {
      id: '5',
      value: 'frappe-hr',
      label: 'Frappe HR',
      description:
        'A modern open-source HRMS built by Frappe, offering modules for employee management, attendance, payroll, performance, and expense management.',
      link: 'https://frappe.io/hr',
      order: 5,
    },
    {
      id: '6',
      value: 'gitea',
      label: 'Gitea',
      description:
        'A lightweight self-hosted Git service for source control, issue tracking, and CI/CD, ideal for teams seeking an easy DevOps setup similar to GitHub.',
      link: 'https://gitea.io',
      order: 6,
    },
    {
      id: '7',
      value: 'gitlab',
      label: 'GitLab CE',
      description:
        'GitLab is a complete DevSecOps lifecycle platform offering source control, CI/CD, issue tracking, and security features under community and enterprise editions.',
      link: 'https://about.gitlab.com',
      order: 7,
    },
    {
      id: '8',
      value: 'grafana',
      label: 'Grafana',
      description:
        'A leading open-source analytics and visualization tool for metrics, logs, and traces dashboards connected to databases like Prometheus and Loki.',
      link: 'https://grafana.com',
      order: 8,
    },
    {
      id: '9',
      value: 'jellyfin',
      label: 'Jellyfin',
      description:
        'Free and open-source media server software for streaming movies, TV shows, and music across devices without cloud dependency.',
      link: 'https://jellyfin.org',
      order: 9,
    },
    {
      id: '10',
      value: 'jenkins',
      label: 'Jenkins',
      description:
        'Popular open-source automation server for CI/CD pipelines, supporting plugin-based integration and automated build processes.',
      link: 'https://www.jenkins.io',
      order: 10,
    },
    {
      id: '11',
      value: 'jitsi-meet',
      label: 'Jitsi Meet',
      description:
        'Open-source video conferencing solution with end-to-end encryption and easy browser-based deployment.',
      link: 'https://meet.jit.si',
      order: 11,
    },
    {
      id: '12',
      value: 'keycloak',
      label: 'Keycloak',
      description:
        'Keycloak is a robust open-source IAM (identity and access management) tool providing SSO, OAuth2, OpenID Connect, and LDAP/AD integration for enterprise applications.',
      link: 'https://www.keycloak.org',
      order: 12,
    },
    {
      id: '13',
      value: 'kimai',
      label: 'Kimai',
      description:
        'Open-source time-tracking software for freelancers and teams, supporting invoicing and reporting.',
      link: 'https://www.kimai.org',
      order: 13,
    },
    {
      id: '14',
      value: 'magento',
      label: 'Magento Open Source',
      description:
        'E-commerce platform supporting product management, catalog design, and flexible checkout under the open-source Adobe brand.',
      link: 'https://business.adobe.com/products/magento/open-source.html',
      order: 14,
    },
    {
      id: '15',
      value: 'mailcow',
      label: 'Mailcow',
      description:
        'Docker-based email suite integrating Postfix, Dovecot, and modern webmail for a full-featured self-hosted mail server.',
      link: 'https://mailcow.email',
      order: 15,
    },
    {
      id: '16',
      value: 'mattermost',
      label: 'Mattermost',
      description:
        'Mattermost is an open-source, self-hostable chat and collaboration platform designed for secure enterprise communication, serving as an alternative to Slack or Microsoft Teams.',
      link: 'https://mattermost.com',
      order: 16,
    },
    {
      id: '17',
      value: 'matomo',
      label: 'Matomo',
      description:
        'An open-source web analytics platform that serves as a self-hosted alternative to Google Analytics, emphasizing data privacy.',
      link: 'https://matomo.org',
      order: 17,
    },
    {
      id: '18',
      value: 'metabase',
      label: 'Metabase',
      description:
        'An open-source business intelligence platform allowing non-technical users to create dashboards and query data easily.',
      link: 'https://www.metabase.com',
      order: 18,
    },
    {
      id: '19',
      value: 'minio',
      label: 'MinIO',
      description:
        'An open-source, high-performance object storage system compatible with Amazon S3 APIs, built for private cloud infrastructure and data lakes.',
      link: 'https://min.io',
      order: 19,
    },
    {
      id: '20',
      value: 'n8n',
      label: 'n8n',
      description:
        'An open-source workflow automation platform enabling integrations and automation across apps and APIs similar to Zapier.',
      link: 'https://n8n.io',
      order: 20,
    },
    {
      id: '21',
      value: 'nextcloud',
      label: 'Nextcloud',
      description:
        'Nextcloud is a self-hosted collaboration platform for file syncing, video calling, calendar, mail, and document editing, ensuring complete data privacy and control.',
      link: 'https://nextcloud.com',
      order: 21,
    },
    {
      id: '22',
      value: 'nextcloud-collabora',
      label: 'Nextcloud + Collabora',
      description:
        'Collabora Online integrates with Nextcloud to provide real-time collaborative document editing based on LibreOffice technology.',
      link: 'https://www.collaboraonline.com',
      order: 22,
    },
    {
      id: '23',
      value: 'odoo',
      label: 'Odoo',
      description:
        'Odoo is an open-source all-in-one business management suite offering modules for CRM, accounting, sales, inventory, HR, and marketing.',
      link: 'https://www.odoo.com',
      order: 23,
    },
    {
      id: '24',
      value: 'ollama',
      label: 'Ollama',
      description:
        'Local AI inference engine for running and managing LLMs directly on your system with privacy-focused deployment.',
      link: 'https://ollama.ai',
      order: 24,
    },
    {
      id: '25',
      value: 'open-webui',
      label: 'Open WebUI',
      description:
        'Web interface for managing local AI models like Ollama, providing chat interface, workflow tools, and extensibility.',
      link: 'https://openwebui.com',
      order: 25,
    },
    {
      id: '26',
      value: 'openproject',
      label: 'OpenProject',
      description:
        'Project management software with Gantt charts, time tracking, Agile boards, and collaboration tools for teams.',
      link: 'https://www.openproject.org',
      order: 26,
    },
    {
      id: '27',
      value: 'penpot',
      label: 'Penpot',
      description:
        'An open-source design and prototyping tool for teams, supporting vector graphics, collaborative workflows, and Figma-like interfaces.',
      link: 'https://penpot.app',
      order: 27,
    },
    {
      id: '28',
      value: 'pi-hole',
      label: 'Pi-hole',
      description:
        'Network-wide ad blocker that filters DNS queries to block unwanted ads and trackers at the network level.',
      link: 'https://pi-hole.net',
      order: 28,
    },
    {
      id: '29',
      value: 'prometheus',
      label: 'Prometheus',
      description:
        'Open-source monitoring and alerting toolkit widely used in DevOps environments for capturing time-series metrics.',
      link: 'https://prometheus.io',
      order: 29,
    },
    {
      id: '30',
      value: 'restic',
      label: 'Restic',
      description:
        'A fast, secure, and efficient backup program written in Go, supporting various storage backends, including S3 and local disks.',
      link: 'https://restic.net',
      order: 30,
    },
    {
      id: '31',
      value: 'rocket-chat',
      label: 'Rocket.Chat',
      description:
        'A self-hosted chat platform supporting team collaboration, audio/video calls, and integrations, used as an open-source alternative to Slack.',
      link: 'https://rocket.chat',
      order: 31,
    },
    {
      id: '32',
      value: 'roundcube',
      label: 'Roundcube',
      description:
        'Browser-based IMAP webmail client with a modern user interface and plugin support.',
      link: 'https://roundcube.net',
      order: 32,
    },
    {
      id: '33',
      value: 'seafile',
      label: 'Seafile',
      description:
        'Self-hosted file synchronization and sharing platform focused on performance and data security.',
      link: 'https://www.seafile.com',
      order: 33,
    },
    {
      id: '34',
      value: 'suitecrm',
      label: 'SuiteCRM',
      description:
        'Open-source CRM for managing leads, accounts, campaigns, and customer support — a free alternative to Salesforce.',
      link: 'https://suitecrm.com',
      order: 34,
    },
    {
      id: '35',
      value: 'uvdesk',
      label: 'UVDesk',
      description:
        'Customer support ticketing system built on the Symfony PHP framework, offering automation and team management features.',
      link: 'https://www.uvdesk.com',
      order: 35,
    },
    {
      id: '36',
      value: 'vault',
      label: 'Vault (HashiCorp)',
      description:
        'Tool for securely storing secrets, managing encryption keys, and controlling access to sensitive data.',
      link: 'https://www.vaultproject.io',
      order: 36,
    },
    {
      id: '37',
      value: 'wireguard',
      label: 'WireGuard',
      description:
        'Modern, fast, and simple VPN protocol designed for high-speed cryptographic network tunneling.',
      link: 'https://www.wireguard.com',
      order: 37,
    },
    {
      id: '38',
      value: 'xwiki',
      label: 'XWiki',
      description:
        'Enterprise-grade open-source wiki and knowledge management platform for collaborative documentation.',
      link: 'https://www.xwiki.org',
      order: 38,
    },
    {
      id: '39',
      value: 'zammad',
      label: 'Zammad',
      description:
        'Open-source helpdesk and ticketing system with email, chat, and social network integration for customer service teams.',
      link: 'https://zammad.com',
      order: 39,
    },
    {
      id: '40',
      value: 'zulip',
      label: 'Zulip',
      description:
        'Open-source chat platform combining threaded messaging with group channels for organized team communication.',
      link: 'https://zulip.com',
      order: 40,
    },
    {
      id: '41',
      value: 'other',
      label: 'Other',
      description: 'Other open-source solutions',
      link: '',
      order: 41,
    },
  ];
}
